"use client";

import { useEffect, useRef, useState } from "react";

// Block right-click ("Save video as…" / "Copy video address") and drag-out.
const block = (e: React.SyntheticEvent) => e.preventDefault();

/**
 * A product clip that shows the WHOLE frame, correctly proportioned, and is
 * hardened against casual downloading.
 *
 * Display: the POSTER <img> defines the box at the clip's natural aspect (full
 * frame, never cropped/stretched, renders instantly). The <video> is overlaid
 * and fades in once it genuinely paints; a fail-safe removes it if it can't.
 *
 * Protection (deterrent, NOT DRM): the MP4 is fetched once and played from a
 * `blob:` URL, so the real file path never appears in the DOM or the right-click
 * menu. Right-click, download, drag and Picture-in-Picture are disabled. (A
 * determined user can still see the fetch in the Network tab or screen-record —
 * only EME/DRM prevents that, and even that can't stop screen recording.)
 */
export function ClipVideo({
  src,
  className = "",
}: {
  src: string;
  className?: string;
}) {
  const ref = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const [failed, setFailed] = useState(false);
  const [blobUrl, setBlobUrl] = useState<string>();
  const poster = src.replace(/\.mp4$/i, ".jpg");

  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    let objUrl: string | undefined;
    let started = false;
    let timer = 0;

    // Fetch the MP4 once (on first view) and hand the <video> a blob: URL so the
    // real path is never exposed in the DOM / right-click menu.
    const killUrl = () => {
      if (objUrl) {
        URL.revokeObjectURL(objUrl);
        objUrl = undefined;
      }
    };

    const loadBlob = () => {
      if (started) return;
      started = true;
      fetch(src)
        .then((r) => r.blob())
        .then((b) => {
          objUrl = URL.createObjectURL(b);
          setBlobUrl(objUrl);
          // Belt-and-braces: revoke after a few seconds even if no buffer event
          // fires. A small in-memory blob is fully loaded into the <video> by
          // then, so it keeps looping while the URL becomes dead/unopenable.
          window.setTimeout(killUrl, 4000);
        })
        .catch(() => setFailed(true));
    };

    // As soon as the whole clip is buffered into the <video>, REVOKE the blob
    // URL. Playback/loop keeps working from the in-memory buffer, but the URL is
    // now dead — copying it from devtools and opening it in a new tab fails. This
    // closes the "open the blob: link directly" hole.
    const revokeWhenBuffered = () => {
      if (!objUrl) return;
      const end = v.buffered.length ? v.buffered.end(v.buffered.length - 1) : 0;
      if (v.duration && end >= v.duration - 0.15) {
        URL.revokeObjectURL(objUrl);
        objUrl = undefined;
        v.removeEventListener("progress", revokeWhenBuffered);
        v.removeEventListener("canplaythrough", revokeWhenBuffered);
      }
    };
    v.addEventListener("progress", revokeWhenBuffered);
    v.addEventListener("canplaythrough", revokeWhenBuffered);

    const reveal = () => {
      if (!v.paused && v.currentTime > 0 && v.readyState >= 2) {
        setPlaying(true);
        window.clearTimeout(timer);
      }
    };
    const hide = () => setPlaying(false);
    v.addEventListener("timeupdate", reveal);
    v.addEventListener("pause", hide);
    v.addEventListener("ended", hide);

    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          loadBlob();
          v.play().catch(() => {});
          window.clearTimeout(timer);
          timer = window.setTimeout(() => {
            if (!(v.currentTime > 0)) setFailed(true);
          }, 8000);
        } else {
          v.pause();
        }
      },
      { threshold: 0.2 }
    );
    io.observe(v);

    return () => {
      v.removeEventListener("timeupdate", reveal);
      v.removeEventListener("pause", hide);
      v.removeEventListener("ended", hide);
      v.removeEventListener("progress", revokeWhenBuffered);
      v.removeEventListener("canplaythrough", revokeWhenBuffered);
      io.disconnect();
      window.clearTimeout(timer);
      if (objUrl) URL.revokeObjectURL(objUrl);
    };
  }, [src]);

  // Autoplay once the blob URL is attached.
  useEffect(() => {
    const v = ref.current;
    if (v && blobUrl) v.play().catch(() => {});
  }, [blobUrl]);

  return (
    <div
      className={"relative w-full select-none bg-background " + className}
      onContextMenu={block}
    >
      {/* Poster defines the box at the clip's natural aspect — full frame,
          never cropped/stretched, renders instantly. */}
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={poster}
        alt=""
        draggable={false}
        onContextMenu={block}
        onDragStart={block}
        className="pointer-events-none block h-auto w-full select-none"
      />
      {/* Video overlay — plays from a blob: URL; faded in once it truly paints. */}
      {!failed && (
        <video
          ref={ref}
          src={blobUrl}
          loop
          muted
          playsInline
          autoPlay
          preload="auto"
          controlsList="nodownload noremoteplayback noplaybackrate"
          disablePictureInPicture
          draggable={false}
          onContextMenu={block}
          onDragStart={block}
          className={
            "pointer-events-none absolute inset-0 h-full w-full object-contain transition-opacity duration-500 " +
            (playing ? "opacity-100" : "opacity-0")
          }
        />
      )}
    </div>
  );
}
